import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import { toast } from '@/stores/toast'
import type {
  ConfirmationsResponse,
  ConfirmParticipantResult,
  DeclineParticipantResult,
} from './bookings-types'

/**
 * Pending confirmations for the authenticated user — the "what's on
 * my plate" feed. Includes both pending rows and the cascaded
 * delegate rows (Phase A returns only self-rows; delegate plumbing is
 * a follow-up that drops in without a client change).
 *
 * Polls every 30s as a low-cost freshness backstop. WebSocket events
 * (CLA-XXX follow-up) will let us invalidate the query in response
 * to push payloads landing instead of polling.
 */
export function useBookingConfirmations() {
  return useQuery({
    queryKey: ['bookings', 'confirmations'],
    queryFn: () => api.get<ConfirmationsResponse>('/me/booking-confirmations'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })
}

export function useConfirmParticipant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (participantId: number) =>
      api.post<ConfirmParticipantResult>(
        `/bookings/participants/${participantId}/confirm`,
      ),
    onSuccess: (_data) => {
      qc.invalidateQueries({ queryKey: ['bookings', 'confirmations'] })
      toast.success('Confirmed')
    },
    onError: (err) => {
      const detail = err instanceof Error ? err.message : 'Something went wrong'
      toast.error(`Couldn't confirm: ${detail}`)
    },
  })
}

export function useDeclineParticipant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ participantId, reason }: { participantId: number; reason?: string }) =>
      api.post<DeclineParticipantResult>(
        `/bookings/participants/${participantId}/decline`,
        reason ? { reason } : undefined,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings', 'confirmations'] })
      toast.success('Declined. We\'ll find an alternate.')
    },
    onError: (err) => {
      const detail = err instanceof Error ? err.message : 'Something went wrong'
      toast.error(`Couldn't decline: ${detail}`)
    },
  })
}
